import { useState, useEffect } from "react";
import Table from 'react-bootstrap/Table';
import { Row, Col, Form, Pagination } from "react-bootstrap";
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import { useAuth } from "../../contexts/AuthContext";
import { Check2 } from 'react-bootstrap-icons';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

function UsersTable() {
  const { searchUsers } = useAuth();
  const [paidUsers, setPaidUsers] = useState([]);
  const [searchData, setSearchData] = useState({search_type: "email", search_term: ""});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  useEffect(() => {
    const retrievePaidUsers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/admin_dashboard`, {
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok) {
          setPaidUsers(data);
          setCurrentPage(1);
        } else {
          console.error("Could not retrieve user data:", data);
        }

      } catch (error) {
        console.error("Error retrieving user data:", error);
      }
    };

    retrievePaidUsers();
  }, [])

  useEffect(() => {
    if ("search_term" in searchData) {
      const timer = setTimeout(() => {
        runSearch();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchData])

  const runSearch = async () => {
    const response = await searchUsers(searchData.search_type, searchData.search_term);
    if (response.success) {
      setPaidUsers(response.paid_users)
      setCurrentPage(1);
    } else {
      console.log(response.message);
    }
  }

  const handleChange = (e) => {
    setSearchData({ ...searchData, [e.target.name]: e.target.value });
    setCurrentPage(1);
  };

  const getEmail = (u) => (u.user?.email || "").toLowerCase();
  const getFirstName = (u) => (u.first_name || "").toLowerCase();
  const getLastName = (u) => (u.last_name || "").toLowerCase();

  const sortedPaidUsers = [...paidUsers].sort((a, b) => {
    if (searchData.search_type === "first_name") {
      return getFirstName(a).localeCompare(getFirstName(b)) ||
             getLastName(a).localeCompare(getLastName(b));
    } else if (searchData.search_type === "last_name") {
      return getLastName(a).localeCompare(getLastName(b)) ||
             getFirstName(a).localeCompare(getFirstName(b));
    } else {
      return getEmail(a).localeCompare(getEmail(b));
    }
  });
  const totalPages = Math.ceil(paidUsers.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = sortedPaidUsers.slice(startIndex, endIndex);

  return (
    <div>
      <Row className="mx-4 mt-5">
        <h2>Profiles</h2>
        <Form>
          <Form.Group as={Row}>
            <Col md={2} className="ml-0 pl-0">
              <FloatingLabel label="Search By">
                <Form.Select
                  name="search_type"
                  value={searchData.search_type || ""}
                  onChange={handleChange}
                >
                  <option value="email">Email</option>
                  <option value="last_name">Last Name</option>
                  <option value="first_name">First Name</option>
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col md={4}>
              <FloatingLabel label="Search">
                <Form.Control
                  name="search_term"
                  value={searchData.search_term || ""}
                  onChange={handleChange}
                />
              </FloatingLabel>
            </Col>
          </Form.Group>
        </Form>
      </Row>

      <Table
        striped
        // bordered
        hover
        style={{
          "background-color": "#dee8faff",
          "marginTop": "50px",
          "marginLeft": "20px",
          "marginRight": "20px"
        }}
      >
        <thead>
          <tr>
            <th>Email</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Paid</th>
            <th>Status/Progress</th>
          </tr>
        </thead>
        <tbody>
          {paginatedUsers.map(paidUser => (
            <tr key={paidUser.id}>
              <td>{(paidUser.user && paidUser.user.email) ? paidUser.user.email : "-"}</td>
              <td>{paidUser.first_name ? paidUser.first_name : "-"}</td>
              <td>{paidUser.last_name ? paidUser.last_name : "-"}</td>
              <td>
                {paidUser.ctg_course_paid ? (
                  <Check2 size={22} className="text-success fw-bold" title="Paid" />
                ) : (
                  ""
                )}
              </td>
              <td>{paidUser.course_progress}</td>
            </tr>
          ))}
        </tbody>

      </Table>
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mx-4 mb-4">
          <span className="text-muted">
          Showing {paidUsers.length === 0 ? 0 : startIndex + 1} to{" "}
          {Math.min(endIndex, paidUsers.length)} of {paidUsers.length} users
          </span>
          <Pagination className="mb-0">
            <Pagination.First
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            />
            <Pagination.Prev
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev -1, 1))}
            />
            {currentPage > 3 && (
              <>
              <Pagination.Item onClick={() => setCurrentPage(1)}>1</Pagination.Item>
              {currentPage > 4 && <Pagination.Ellipsis disabled />}
              </>
            )}

            {Array.from({ length: totalPages }, (_, index) => index + 1)
              .filter(p => Math.abs(p - currentPage) <= 2)
              .map(pageNumber => (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === currentPage}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              ))}

            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && <Pagination.Ellipsis disabled />}
                <Pagination.Item onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>
                </>
            )}
            <Pagination.Next
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            />
            <Pagination.Last
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default UsersTable;
